import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse, isAxiosError } from 'axios';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { Dispatch, SetStateAction, useState } from 'react';

import { postPrizeEntryApi } from '@flipster/apis/v1/campaign';
import { GetPrizeEntryResponse, PrizeEntryItem } from '@flipster/apis/v1/campaign/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Modal } from '@flipster/react/design-system/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import { css } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { ServerError } from '@flipster/utils/constants/server-error';
import { numberFormatter } from '@flipster/utils/num';

interface SubmitPrizeEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: PrizeEntryItem;
  availableTickets: number;
  onExit: () => void;
}

export const SubmitPrizeEntryModal = ({
  isOpen,
  onClose,
  item,
  availableTickets,
  onExit,
}: SubmitPrizeEntryModalProps) => {
  const t = useTranslations();
  const router = useRouter();
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();

  const [entries, setEntries] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { isCookieLoggedIn } = useAuth();

  const { mutateAsync: submitPrizeEntry } = useMutation({
    mutationKey: ['postPrizeEntryApi'],
    mutationFn: postPrizeEntryApi,
    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        ['getPrizeEntryApi', slug, isCookieLoggedIn],
        (old: AxiosResponse<GetPrizeEntryResponse>) => ({
          ...old,
          data: {
            ...old.data,
            myTicket: data.myTicket,
            items: old.data.items.map((item) =>
              item.id === data.item.id
                ? {
                    ...item,
                    totalEntries: data.item.totalEntries,
                    yourEntries: data.item.yourEntries,
                  }
                : item,
            ),
          },
        }),
      );
      snackbar.success({
        message: (
          <FormattedMessage id='Campaign.r251127.Success_Submit_Entry' values={{ number: entries, prize: item.name }} />
        ),
      });
    },
    onError: (error) => {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 400:
          snackbar.error({ message: <FormattedMessage id='Campaign.r251127.Failed_Submit_Entry_400' /> });
          break;
        case 401:
          snackbar.error({ message: <FormattedMessage id='Common.r250918.Please_log_in' /> });
          router.push('/login');
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
      queryClient.refetchQueries({ queryKey: ['getPrizeEntryApi', slug, isCookieLoggedIn] });
    },
  });

  const validateEntries = (next: number) => {
    let error = null;
    if (next > availableTickets) {
      error = t('Campaign.r251127.Error_Enough_Entries');
    }
    setError(error);
    return error;
  };

  const changeEntriesInput = (value: string) => {
    const next = value === '' ? 0 : Number(value);
    setEntries(next);
    validateEntries(next);
  };

  const handleSubmit = () => {
    if (validateEntries(entries)) return;
    submitPrizeEntry({ eventId: slug, itemId: item.id, count: entries });
    onClose();
  };

  return (
    <Modal.Root onClose={onClose} isOpen={isOpen} onExit={onExit}>
      <Modal.Header>
        <Modal.Title
          label={
            (item.yourEntries ?? 0) > 0 ? (
              <FormattedMessage id='Campaign.r251127.Add_entry_amount' />
            ) : (
              <FormattedMessage id='Campaign.r251127.Submit_entry_amount' />
            )
          }
        />
      </Modal.Header>
      <Modal.Content>
        <div
          className={css({
            tabletDown: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              flexDirection: 'column',
            },
          })}
        >
          <div className={center({ w: '100%', gap: 4, tablet: { mt: 40 } })}>
            <FormattedNumberInput
              value={entries === 0 ? '' : entries.toString()}
              onChange={changeEntriesInput}
              placeholder='0'
              inputMode='numeric'
              className={css({
                textAlign: 'right',
                textStyle: 'Title/M',
                color: 'content.default.level-5',
                caretColor: 'content.accent.info',
              })}
              style={{
                width: `${Math.max(1, entries.toString().length ?? 0)}ch`,
              }}
            />
            <span className={css({ textStyle: 'Title/M', color: 'content.default.level-5' })}>
              <FormattedMessage id='Campaign.r251127.Entries' />
            </span>
          </div>
          <p className={css({ textStyle: 'Text/M/Regular', textAlign: 'center', mt: 12 })}>
            {error ? (
              <span className={css({ color: 'content.state.negative' })}>{error}</span>
            ) : (
              <span className={css({ color: 'content.default.level-2' })}>
                <FormattedMessage id='Campaign.r251127.Your_available_draw_entries' />
                {': '}
                {numberFormatter(availableTickets)}
              </span>
            )}
          </p>
          <SelectEntryButtons
            availableTickets={availableTickets}
            entries={entries}
            setEntries={setEntries}
            validateEntries={validateEntries}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          label={<FormattedMessage id='Common.r250401.Confirm' />}
          onClick={handleSubmit}
          disabled={entries === 0 || Boolean(error)}
        />
      </Modal.Footer>
    </Modal.Root>
  );
};

interface SelectEntryButtonsProps {
  availableTickets: number;
  entries: number;
  setEntries: Dispatch<SetStateAction<number>>;
  validateEntries: (next: number) => string | null;
}

const SelectEntryButtons = ({ availableTickets, entries, setEntries, validateEntries }: SelectEntryButtonsProps) => {
  const selectEntryButtonItems = [
    { label: '+1', value: '1' },
    { label: '+5', value: '5' },
    { label: '+10', value: '10' },
    { label: 'All', value: 'all' },
  ];

  const addEntries = (value: string) => {
    let next;
    if (value === 'all') next = availableTickets;
    else next = entries + Number(value);
    setEntries(next);
    validateEntries(next);
  };

  return (
    <div className={flex({ gap: 6, mt: 40, w: '100%' })}>
      {selectEntryButtonItems.map((item) => {
        return (
          <button
            type='button'
            key={item.value}
            onClick={() => addEntries(item.value)}
            className={center({
              flex: 1,
              p: 10,
              rounded: 4,
              bgColor: 'background.default.level-3',
            })}
          >
            <span
              className={css({
                textStyle: 'Text/S/Strong',
                color: 'content.default.level-2',
                textAlign: 'center',
              })}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
